'use client'
import { type FragmentInstance, type RefObject, useEffect, useEffectEvent } from 'react'

type Callback = (isInView: boolean) => void

type ObserverOptions = { rootMargin?: string; threshold: number }

type Props = ObserverOptions & { frozen: boolean; callback: Callback }

type ObserverTarget = Element | FragmentInstance

type FragmentElement = Element & { reactFragments?: Set<FragmentInstance> }

// for Fragments, each callback describes an individual DOM child's intersection
export const useIntersectionObserver = <TElement extends ObserverTarget>(
  elementRef: RefObject<TElement | null>,
  { frozen, rootMargin = '0px', threshold, callback }: Props,
) => {
  const callbackEvent = useEffectEvent(callback)

  useEffect(() => {
    const element = elementRef.current
    if (!element || frozen) return

    const { id, observer, elementsMap } = getObserverInstance({
      threshold,
      rootMargin,
    })

    elementsMap.set(element, callbackEvent)

    if ('observeUsing' in element) {
      element.observeUsing(observer)
    } else {
      observer.observe(element)
    }

    return () => {
      if (elementsMap.has(element)) {
        elementsMap.delete(element)

        if ('unobserveUsing' in element) {
          element.unobserveUsing(observer)
        } else {
          observer.unobserve(element)
        }
      }

      if (elementsMap.size === 0) {
        observer.disconnect()
        observersMap.delete(id)
      }
    }
  }, [elementRef, rootMargin, threshold, frozen])
}

type ObserverInstance = {
  id: string
  observer: IntersectionObserver
  // keep callbacks scoped to this observer's configuration
  elementsMap: Map<ObserverTarget, Callback>
}
const observersMap = new Map<string, ObserverInstance>()

const getObserverInstance = ({ threshold, rootMargin }: ObserverOptions): ObserverInstance => {
  const id = `${rootMargin}_${threshold}`
  const existingObserver = observersMap.get(id)
  if (existingObserver) return existingObserver

  const elementsMap: ObserverInstance['elementsMap'] = new Map()

  const observer = new window.IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
        const isInView = isIntersecting && intersectionRatio >= threshold

        const callback = elementsMap.get(target)
        callback?.(isInView)

        const element: FragmentElement = target

        element.reactFragments?.forEach((fragmentInstance) => {
          const fragmentCallback = elementsMap.get(fragmentInstance)
          fragmentCallback?.(isInView)
        })
      })
    },
    { root: null, rootMargin, threshold },
  )
  const instance: ObserverInstance = { id, observer, elementsMap }
  observersMap.set(id, instance)

  return instance
}
